import { Fragment, useCallback, useMemo, useState } from "react";
import sanitizeHtml from "sanitize-html";
import { cn } from "../../utils/bem";
import { absoluteURL, copyText } from "../../utils/helpers";
import { Button } from "@humansignal/ui";
import { Space } from "../Space/Space";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./Error.prefix.css";

// Community help channel shown on the error screen.
const DISCORD_URL = "https://discord.com/channels/1499505378885505164/1499505386523459596";

// Inline Discord glyph — the shared icon set doesn't ship one.
const IconDiscord = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden {...props}>
    <path d="M19.27 5.33A16.6 16.6 0 0 0 15.1 4l-.2.38a11.5 11.5 0 0 1 3.6 1.14 15.6 15.6 0 0 0-13 0A11.5 11.5 0 0 1 9.1 4.4L8.9 4a16.6 16.6 0 0 0-4.17 1.33C1.9 9.29.99 13.13 1.29 16.92a16.7 16.7 0 0 0 5.06 2.55l.4-.9a.13.13 0 0 0-.07-.18 10.9 10.9 0 0 1-1.55-.74.13.13 0 0 1-.01-.22l.3-.24a11.9 11.9 0 0 0 10.16 0l.31.24a.13.13 0 0 1 0 .22c-.5.29-1.02.54-1.56.74a.13.13 0 0 0-.07.18l.4.9a16.6 16.6 0 0 0 5.06-2.55c.36-4.38-.6-8.19-2.72-11.59ZM8.52 14.6c-.99 0-1.8-.9-1.8-2.02 0-1.11.8-2.02 1.8-2.02 1.01 0 1.82.92 1.8 2.02 0 1.11-.8 2.02-1.8 2.02Zm6.96 0c-.99 0-1.8-.9-1.8-2.02 0-1.11.8-2.02 1.8-2.02 1.01 0 1.82.92 1.8 2.02 0 1.11-.79 2.02-1.8 2.02Z" />
  </svg>
);

export const ErrorWrapper = ({
  title,
  message,
  errorId,
  stacktrace,
  validation,
  version,
  onGoBack,
  onReload,
  possum = false,
  minimal = false,
}) => {
  const { t } = useTranslation();
  const preparedStackTrace = useMemo(() => {
    return (stacktrace ?? "").trim();
  }, [stacktrace]);

  const [copied, setCopied] = useState(false);

  const copyStacktrace = useCallback(() => {
    setCopied(true);
    copyText(preparedStackTrace);
    setTimeout(() => setCopied(false), 1200);
  }, [preparedStackTrace]);

  return (
    <div className={cn("error-message").toClassName()}>
      {!minimal && possum !== false && (
        <img
          className={cn("error-message").elem("heidi").toClassName()}
          src={absoluteURL("/static/brand/afriannotate/mark.png")}
          height="96"
          alt=""
          aria-hidden="true"
        />
      )}

      {!minimal && title && <div className={cn("error-message").elem("title").toClassName()}>{title}</div>}

      {!minimal && message && (
        <div
          className={cn("error-message").elem("detail").toClassName()}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(String(message)),
          }}
        />
      )}

      {!minimal && preparedStackTrace && (
        <div
          className={cn("error-message").elem("stracktrace").toClassName()}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(preparedStackTrace.replace(/(\n)/g, "<br>")),
          }}
        />
      )}

      {validation?.length > 0 && (
        <ul className={cn("error-message").elem("validation").toClassName()}>
          {validation.map(([field, errors]) => (
            <Fragment key={field}>
              {[].concat(errors).map((err, i) => (
                <li
                  key={i}
                  className={cn("error-message").elem("message").toClassName()}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(err) }}
                />
              ))}
            </Fragment>
          ))}
        </ul>
      )}

      {!minimal && (version || errorId) && (
        <div className={cn("error-message").elem("version").toClassName()}>
          <Space>
            {version && `Version: ${version}`}
            {errorId && `Error ID: ${errorId}`}
          </Space>
        </div>
      )}

      {!minimal && (
        <div className={cn("error-message").elem("actions").toClassName()}>
          <Space spread>
            <Button
              className={cn("error-message").elem("action-slack").toClassName()}
              target="_blank"
              icon={<IconDiscord />}
              href={DISCORD_URL}
            >
              Ask on Discord
            </Button>

            <Space size="small">
              {preparedStackTrace && (
                <Button
                  disabled={copied}
                  onClick={copyStacktrace}
                  className="w-[100px]"
                  aria-label={t("components.error.error.copy_error_stacktrace")}
                >
                  {copied ? "Copied" : "Copy Stacktrace"}
                </Button>
              )}
              {onGoBack && (
                <Button onClick={onGoBack} aria-label={t("components.error.error.go_back")}>
                  Go Back
                </Button>
              )}
              {onReload && (
                <Button onClick={onReload} aria-label={t("components.error.error.reload_page")}>
                  Reload
                </Button>
              )}
            </Space>
          </Space>
        </div>
      )}
    </div>
  );
};
